import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import authService from '../services/authService';
import Navbar from './Navbar';
import Footer from './Footer';

const SuperAdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('board-games');
  const [selectedItem, setSelectedItem] = useState(null);
  const [data, setData] = useState({
    boardGameRequests: [],
    clubRequests: [],
    discussions: [],
    allBoardGames: []
  });
  const [allClubs, setAllClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '', description: '', clubId: '', location: '',
    startDate: '', endDate: '', startTime: '', endTime: '',
    maxAttendees: '', imageUrl: '', status: 'published'
  });
  const [eventFormLoading, setEventFormLoading] = useState(false);
  const [eventFormError, setEventFormError] = useState('');
  const [eventFormSuccess, setEventFormSuccess] = useState('');
  const [editingEvent, setEditingAdminEvent] = useState(null);
  const [stats, setStats] = useState({
    pendingGames: 0,
    pendingClubs: 0,
    totalDiscussions: 0,
    totalBoardGames: 0
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddGameModal, setShowAddGameModal] = useState(false);
  const [showEditGameModal, setShowEditGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeSection, searchTerm]);

  useEffect(() => {
    if (activeSection === 'events') {
      fetchAdminEvents();
      fetchAllClubs();
    }
  }, [activeSection]);

  const fetchAllClubs = async () => {
    try {
      const res = await authService.get('/api/clubs');
      if (res.success) setAllClubs(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchAdminEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await fetch((process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/events');
      const data = await res.json();
      if (data.success) setEvents(data.data);
    } catch (e) { console.error(e); }
    finally { setEventsLoading(false); }
  };

  const handleAdminCreateEvent = async (e) => {
    e.preventDefault();
    setEventFormError('');
    setEventFormSuccess('');
    if (!eventForm.title || !eventForm.clubId || !eventForm.startDate || !eventForm.startTime) {
      setEventFormError('Title, club, start date and start time are required.');
      return;
    }
    setEventFormLoading(true);
    try {
      const payload = {
        ...eventForm,
        clubId: parseInt(eventForm.clubId),
        maxAttendees: eventForm.maxAttendees ? parseInt(eventForm.maxAttendees) : null,
        endDate: eventForm.endDate || null,
        endTime: eventForm.endTime || null,
        imageUrl: eventForm.imageUrl || null
      };
      let res;
      if (editingEvent) {
        res = await authService.put(
          (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + `/events/${editingEvent.id}`,
          payload
        );
      } else {
        res = await authService.post(
          (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/events',
          payload
        );
      }
      if (res.success) {
        setEventFormSuccess(editingEvent ? 'Event updated!' : 'Event created!');
        setEventForm({ title: '', description: '', clubId: '', location: '', startDate: '', endDate: '', startTime: '', endTime: '', maxAttendees: '', imageUrl: '', status: 'published' });
        setEditingAdminEvent(null);
        fetchAdminEvents();
      } else {
        setEventFormError(res.message || 'Failed to save event');
      }
    } catch (err) {
      setEventFormError(err.message);
    } finally {
      setEventFormLoading(false);
    }
  };

  const handleAdminDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await authService.delete(
        (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + `/events/${id}`
      );
      fetchAdminEvents();
    } catch (e) { alert('Failed to delete event'); }
  };

  const handleAdminEditEvent = (ev) => {
    setEditingAdminEvent(ev);
    setEventForm({
      title: ev.title || '',
      description: ev.description || '',
      clubId: String(ev.clubId || ''),
      location: ev.location || '',
      startDate: ev.startDate ? ev.startDate.slice(0,10) : '',
      endDate: ev.endDate ? ev.endDate.slice(0,10) : '',
      startTime: ev.startTime || '',
      endTime: ev.endTime || '',
      maxAttendees: ev.maxAttendees !== null && ev.maxAttendees !== undefined ? String(ev.maxAttendees) : '',
      imageUrl: ev.imageUrl || '',
      status: ev.status || 'published'
    });
    setEventFormError('');
    setEventFormSuccess('');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const promises = [
        authService.get('/api/super-admin/board-game-requests?status=pending'),
        authService.get('/api/super-admin/pending-clubs'),
        authService.get('/api/discussions'),
        authService.get('/api/super-admin/stats')
      ];

      // Add all board games fetch when in manage-board-games section
      if (activeSection === 'manage-board-games') {
        promises.push(authService.get(`/api/board-games?limit=100&includeAll=true${searchTerm ? `&search=${searchTerm}` : ''}`));
      }

      const results = await Promise.all(promises);
      const [bgRequests, clubRequests, discussionsData, statsData, allGames] = results;

      setData({
        boardGameRequests: bgRequests.data || [],
        clubRequests: clubRequests.data || [],
        discussions: discussionsData.data || [],
        allBoardGames: allGames?.data || []
      });

      setStats(statsData.data || { pendingGames: 0, pendingClubs: 0, totalDiscussions: 0, totalBoardGames: 0 });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveGame = async (id) => {
    setActionLoading(true);
    try {
      await authService.post(`/api/super-admin/board-game-requests/${id}/approve`);
      await fetchData();
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to approve game:', error);
      alert(error.response?.data?.message || 'Failed to approve game');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectGame = async (id, reason) => {
    if (!reason) {
      alert('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await authService.post(`/api/super-admin/board-game-requests/${id}/reject`, { rejectionReason: reason });
      await fetchData();
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to reject game:', error);
      alert(error.response?.data?.message || 'Failed to reject game');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveClub = async (id) => {
    setActionLoading(true);
    try {
      await authService.post(`/api/super-admin/clubs/${id}/approve`);
      await fetchData();
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to approve club:', error);
      alert(error.response?.data?.message || 'Failed to approve club');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClub = async (id, reason) => {
    if (!reason) {
      alert('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await authService.post(`/api/super-admin/clubs/${id}/reject`, { rejectionReason: reason });
      await fetchData();
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to reject club:', error);
      alert(error.response?.data?.message || 'Failed to reject club');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTogglePin = async (id) => {
    setActionLoading(true);
    try {
      await authService.post(`/api/super-admin/discussions/${id}/toggle-pin`);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      alert(error.response?.data?.message || 'Failed to toggle pin');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleLock = async (id) => {
    setActionLoading(true);
    try {
      await authService.post(`/api/super-admin/discussions/${id}/toggle-lock`);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle lock:', error);
      alert(error.response?.data?.message || 'Failed to toggle lock');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDiscussion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this discussion?')) return;
    setActionLoading(true);
    try {
      await authService.delete(`/api/super-admin/discussions/${id}`);
      await fetchData();
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to delete discussion:', error);
      alert(error.response?.data?.message || 'Failed to delete discussion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddBoardGame = () => {
    setShowAddGameModal(true);
  };

  const handleEditBoardGame = (game) => {
    setEditingGame(game);
    setShowEditGameModal(true);
  };

  const handleDeleteBoardGame = async (id) => {
    if (!window.confirm('Are you sure you want to delete this board game? This will remove it from all clubs.')) return;
    setActionLoading(true);
    try {
      await authService.delete(`/api/board-games/${id}`);
      await fetchData();
      setSelectedItem(null);
      alert('Board game deleted successfully');
    } catch (error) {
      console.error('Failed to delete board game:', error);
      alert(error.response?.data?.message || 'Failed to delete board game');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveBoardGame = async (gameData) => {
    setActionLoading(true);
    try {
      const formData = new FormData();
      Object.keys(gameData).forEach(key => {
        if (gameData[key] !== null && gameData[key] !== undefined) {
          formData.append(key, gameData[key]);
        }
      });

      if (editingGame) {
        // Update existing game
        await authService.put(`/api/board-games/${editingGame.id}`, formData);
        alert('Board game updated successfully');
      } else {
        // Create new game
        await authService.post('/api/board-games', formData);
        alert('Board game added successfully');
      }
      
      setShowAddGameModal(false);
      setShowEditGameModal(false);
      setEditingGame(null);
      await fetchData();
    } catch (error) {
      console.error('Failed to save board game:', error);
      alert(error.response?.data?.message || 'Failed to save board game');
    } finally {
      setActionLoading(false);
    }
  };

  const renderMasterList = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      );
    }

    switch (activeSection) {
      case 'board-games':
        return (
          <div className="space-y-2">
            {data.boardGameRequests.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                <p>No pending board game requests</p>
              </div>
            ) : (
              data.boardGameRequests.map((game) => (
                <motion.div
                  key={game.id}
                  whileHover={{ scale: 1.01 }}
                  onClick={() => setSelectedItem(game)}
                  className={`card bg-base-100 shadow-sm cursor-pointer border-2 transition-all ${
                    selectedItem?.id === game.id ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{game.name}</h3>
                        <p className="text-sm text-base-content/60 line-clamp-2">{game.description}</p>
                        <div className="flex gap-4 mt-2 text-xs text-base-content/60">
                          <span>👥 {game.minPlayers}-{game.maxPlayers} players</span>
                          {game.duration && <span>⏱️ {game.duration} min</span>}
                          {game.complexity && <span>🎯 {game.complexity}</span>}
                        </div>
                        {game.requestedBy && (
                          <p className="text-xs text-base-content/50 mt-1">
                            Requested by {game.requestedBy.firstName} {game.requestedBy.lastName}
                          </p>
                        )}
                      </div>
                      <div className="badge badge-warning">Pending</div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        );

      case 'clubs':
        return (
          <div className="space-y-2">
            {data.clubRequests.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                <p>No pending club requests</p>
              </div>
            ) : (
              data.clubRequests.map((club) => (
                <motion.div
                  key={club.id}
                  whileHover={{ scale: 1.01 }}
                  onClick={() => setSelectedItem(club)}
                  className={`card bg-base-100 shadow-sm cursor-pointer border-2 transition-all ${
                    selectedItem?.id === club.id ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{club.name}</h3>
                        <p className="text-sm text-base-content/60 line-clamp-2">{club.description}</p>
                        <div className="flex gap-4 mt-2 text-xs text-base-content/60">
                          {club.location && <span>📍 {club.location}</span>}
                          {club.meetingDay && <span>📅 {club.meetingDay}</span>}
                        </div>
                      </div>
                      <div className="badge badge-warning">Pending</div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        );

      case 'discussions':
        return (
          <div className="space-y-2">
            {data.discussions.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                <p>No discussions found</p>
              </div>
            ) : (
              data.discussions.map((discussion) => (
                <motion.div
                  key={discussion.id}
                  whileHover={{ scale: 1.01 }}
                  onClick={() => setSelectedItem(discussion)}
                  className={`card bg-base-100 shadow-sm cursor-pointer border-2 transition-all ${
                    selectedItem?.id === discussion.id ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{discussion.title}</h3>
                          {discussion.isPinned && <span className="badge badge-info badge-sm">📌 Pinned</span>}
                          {discussion.isLocked && <span className="badge badge-error badge-sm">🔒 Locked</span>}
                        </div>
                        <p className="text-sm text-base-content/60 line-clamp-2">{discussion.content}</p>
                        <div className="flex gap-4 mt-2 text-xs text-base-content/60">
                          <span>👍 {discussion.upvotes || 0} upvotes</span>
                          <span>💬 {discussion.replyCount || 0} replies</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        );

      case 'manage-board-games':
        return (
          <div className="space-y-2">
            {data.allBoardGames.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                <p>No board games found</p>
              </div>
            ) : (
              data.allBoardGames.map((game) => (
                <motion.div
                  key={game.id}
                  whileHover={{ scale: 1.01 }}
                  onClick={() => setSelectedItem(game)}
                  className={`card bg-base-100 shadow-sm cursor-pointer border-2 transition-all ${
                    selectedItem?.id === game.id ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{game.name}</h3>
                        <p className="text-sm text-base-content/60 line-clamp-2">{game.description}</p>
                        <div className="flex gap-4 mt-2 text-xs text-base-content/60">
                          <span>👥 {game.minPlayers}-{game.maxPlayers} players</span>
                          {game.duration && <span>⏱️ {game.duration} min</span>}
                          {game.complexity && <span>🎯 {game.complexity}</span>}
                        </div>
                      </div>
                      <div className={`badge ${
                        game.approvalStatus === 'approved' ? 'badge-success' :
                        game.approvalStatus === 'pending' ? 'badge-warning' :
                        'badge-error'
                      }`}>
                        {game.approvalStatus}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderDetailPanel = () => {
    if (!selectedItem) {
      return (
        <div className="flex items-center justify-center h-full text-base-content/40">
          <div className="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <p className="text-lg">Select an item to view details</p>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'board-games':
        return <BoardGameDetail game={selectedItem} onApprove={handleApproveGame} onReject={handleRejectGame} loading={actionLoading} />;
      case 'clubs':
        return <ClubDetail club={selectedItem} onApprove={handleApproveClub} onReject={handleRejectClub} loading={actionLoading} />;
      case 'discussions':
        return (
          <DiscussionDetail 
            discussion={selectedItem} 
            onTogglePin={handleTogglePin}
            onToggleLock={handleToggleLock}
            onDelete={handleDeleteDiscussion}
            loading={actionLoading}
          />
        );
      case 'manage-board-games':
        return (
          <ManageBoardGameDetail
            game={selectedItem}
            onEdit={handleEditBoardGame}
            onDelete={handleDeleteBoardGame}
            loading={actionLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-base-200">
      <Navbar />
      
      <div className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2">Super Admin Dashboard</h1>
          <p className="text-base-content/60">Manage board game requests, club approvals, and discussions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="stat bg-base-100 shadow rounded-lg">
            <div className="stat-figure text-warning">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-8 h-8 stroke-current">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
              </svg>
            </div>
            <div className="stat-title">Pending Board Games</div>
            <div className="stat-value text-warning">{stats.pendingGames}</div>
          </div>

          <div className="stat bg-base-100 shadow rounded-lg">
            <div className="stat-figure text-info">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-8 h-8 stroke-current">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div className="stat-title">Pending Clubs</div>
            <div className="stat-value text-info">{stats.pendingClubs}</div>
          </div>

          <div className="stat bg-base-100 shadow rounded-lg">
            <div className="stat-figure text-success">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-8 h-8 stroke-current">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
            </div>
            <div className="stat-title">Total Discussions</div>
            <div className="stat-value text-success">{stats.totalDiscussions}</div>
          </div>

          <div className="stat bg-base-100 shadow rounded-lg">
            <div className="stat-figure text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-8 h-8 stroke-current">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="stat-title">Total Board Games</div>
            <div className="stat-value text-primary">{stats.totalBoardGames}</div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="tabs tabs-boxed bg-base-100 shadow mb-4 p-1">
          <button 
            className={`tab tab-lg flex-1 ${activeSection === 'board-games' ? 'tab-active' : ''}`}
            onClick={() => { setActiveSection('board-games'); setSelectedItem(null); setSearchTerm(''); }}
          >
            🎲 Game Requests ({stats.pendingGames})
          </button>
          <button 
            className={`tab tab-lg flex-1 ${activeSection === 'manage-board-games' ? 'tab-active' : ''}`}
            onClick={() => { setActiveSection('manage-board-games'); setSelectedItem(null); }}
          >
            📚 All Games ({stats.totalBoardGames})
          </button>
          <button 
            className={`tab tab-lg flex-1 ${activeSection === 'clubs' ? 'tab-active' : ''}`}
            onClick={() => { setActiveSection('clubs'); setSelectedItem(null); setSearchTerm(''); }}
          >
            🏛️ Clubs ({stats.pendingClubs})
          </button>
          <button 
            className={`tab tab-lg flex-1 ${activeSection === 'discussions' ? 'tab-active' : ''}`}
            onClick={() => { setActiveSection('discussions'); setSelectedItem(null); setSearchTerm(''); }}
          >
            💬 Discussions ({stats.totalDiscussions})
          </button>
          <button 
            className={`tab tab-lg flex-1 ${activeSection === 'events' ? 'tab-active' : ''}`}
            onClick={() => { setActiveSection('events'); setSelectedItem(null); setSearchTerm(''); }}
          >
            🎉 Events
          </button>
        </div>

        {/* Events Section (full-width) */}
        {activeSection === 'events' && (
          <div className="space-y-6">
            {/* Create / Edit Event Form */}
            <div className="bg-base-100 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">{editingEvent ? 'Edit Event' : 'Create Event on Behalf of a Club'}</h2>
              {eventFormError && <div className="alert alert-error mb-4 py-2 text-sm">{eventFormError}</div>}
              {eventFormSuccess && <div className="alert alert-success mb-4 py-2 text-sm">{eventFormSuccess}</div>}
              <form onSubmit={handleAdminCreateEvent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label"><span className="label-text font-medium">Event Title *</span></label>
                  <input type="text" className="input input-bordered w-full" value={eventForm.title}
                    onChange={e => setEventForm({...eventForm, title: e.target.value})} required />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Club *</span></label>
                  <select className="select select-bordered w-full" value={eventForm.clubId}
                    onChange={e => setEventForm({...eventForm, clubId: e.target.value})} required>
                    <option value="">Select a club…</option>
                    {allClubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label"><span className="label-text font-medium">Description</span></label>
                  <textarea className="textarea textarea-bordered w-full" rows={3} value={eventForm.description}
                    onChange={e => setEventForm({...eventForm, description: e.target.value})} />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Location</span></label>
                  <input type="text" className="input input-bordered w-full" value={eventForm.location}
                    onChange={e => setEventForm({...eventForm, location: e.target.value})} />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Status</span></label>
                  <select className="select select-bordered w-full" value={eventForm.status}
                    onChange={e => setEventForm({...eventForm, status: e.target.value})}>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Start Date *</span></label>
                  <input type="date" className="input input-bordered w-full" value={eventForm.startDate}
                    onChange={e => setEventForm({...eventForm, startDate: e.target.value})} required />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">End Date</span></label>
                  <input type="date" className="input input-bordered w-full" value={eventForm.endDate}
                    onChange={e => setEventForm({...eventForm, endDate: e.target.value})} min={eventForm.startDate} />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Start Time *</span></label>
                  <input type="time" className="input input-bordered w-full" value={eventForm.startTime}
                    onChange={e => setEventForm({...eventForm, startTime: e.target.value})} required />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">End Time</span></label>
                  <input type="time" className="input input-bordered w-full" value={eventForm.endTime}
                    onChange={e => setEventForm({...eventForm, endTime: e.target.value})} />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Max Attendees</span></label>
                  <input type="number" min="1" className="input input-bordered w-full" placeholder="Unlimited" value={eventForm.maxAttendees}
                    onChange={e => setEventForm({...eventForm, maxAttendees: e.target.value})} />
                </div>
                <div>
                  <label className="label"><span className="label-text font-medium">Image URL</span></label>
                  <input type="url" className="input input-bordered w-full" placeholder="https://…" value={eventForm.imageUrl}
                    onChange={e => setEventForm({...eventForm, imageUrl: e.target.value})} />
                </div>
                <div className="md:col-span-2 flex gap-3 pt-2">
                  <button type="submit" className="btn btn-primary flex-1" disabled={eventFormLoading}>
                    {eventFormLoading ? <span className="loading loading-spinner"></span> : editingEvent ? 'Update Event' : 'Create Event'}
                  </button>
                  {editingEvent && (
                    <button type="button" className="btn btn-ghost" onClick={() => { setEditingAdminEvent(null); setEventForm({ title: '', description: '', clubId: '', location: '', startDate: '', endDate: '', startTime: '', endTime: '', maxAttendees: '', imageUrl: '', status: 'published' }); }}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Events List */}
            <div className="bg-base-100 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">All Events</h2>
              {eventsLoading ? (
                <div className="flex justify-center py-10"><span className="loading loading-spinner loading-lg text-primary"></span></div>
              ) : events.length === 0 ? (
                <p className="text-center py-10 text-base-content/50">No events yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table table-zebra w-full text-sm">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Club</th>
                        <th>Date &amp; Time</th>
                        <th>Location</th>
                        <th>RSVPs</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map(ev => (
                        <tr key={ev.id}>
                          <td className="font-medium max-w-[180px] truncate">{ev.title}</td>
                          <td>{ev.club?.name || '—'}</td>
                          <td className="whitespace-nowrap">
                            {new Date(ev.startDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                            <br/><span className="text-xs text-base-content/60">{ev.startTime}{ev.endTime ? ` – ${ev.endTime}` : ''}</span>
                          </td>
                          <td className="max-w-[120px] truncate">{ev.location || '—'}</td>
                          <td>{ev._count?.rsvps ?? 0}{ev.maxAttendees ? ` / ${ev.maxAttendees}` : ''}</td>
                          <td><span className={`badge badge-sm ${
                            ev.status === 'published' ? 'badge-success' :
                            ev.status === 'draft' ? 'badge-warning' : 'badge-error'
                          }`}>{ev.status}</span></td>
                          <td>
                            <div className="flex gap-1">
                              <button className="btn btn-xs btn-ghost" onClick={() => handleAdminEditEvent(ev)}>Edit</button>
                              <button className="btn btn-xs btn-error btn-ghost" onClick={() => handleAdminDeleteEvent(ev.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Master-Detail Layout */}
        {activeSection !== 'events' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Master List */}
          <div className="bg-base-100 shadow rounded-lg p-4 h-[600px] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {activeSection === 'board-games' && 'Board Game Requests'}
                {activeSection === 'manage-board-games' && 'All Board Games'}
                {activeSection === 'clubs' && 'Club Approval Requests'}
                {activeSection === 'discussions' && 'All Discussions'}
              </h2>
              {activeSection === 'manage-board-games' && (
                <button
                  onClick={handleAddBoardGame}
                  className="btn btn-primary btn-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Game
                </button>
              )}
            </div>
            {activeSection === 'manage-board-games' && (
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search board games..."
                  className="input input-bordered w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}
            {renderMasterList()}
          </div>

          {/* Detail Panel */}
          <div className="bg-base-100 shadow rounded-lg p-6 h-[600px] overflow-y-auto">
            {renderDetailPanel()}
          </div>
        </div>
        )}
      </div>

      {/* Add/Edit Board Game Modal */}
      <AddEditBoardGameModal
        isOpen={showAddGameModal || showEditGameModal}
        onClose={() => {
          setShowAddGameModal(false);
          setShowEditGameModal(false);
          setEditingGame(null);
        }}
        onSave={handleSaveBoardGame}
        game={editingGame}
        loading={actionLoading}
      />

      <Footer />
    </div>
  );
};

// Manage Board Game Detail Component
const ManageBoardGameDetail = ({ game, onEdit, onDelete, loading }) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{game.name}</h2>
        <div className={`badge ${
          game.approvalStatus === 'approved' ? 'badge-success' :
          game.approvalStatus === 'pending' ? 'badge-warning' :
          'badge-error'
        }`}>
          {game.approvalStatus}
        </div>
      </div>

      {game.coverImage && (
        <img src={game.coverImage} alt={game.name} className="w-full h-48 object-cover rounded-lg" />
      )}

      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-sm text-base-content/60 mb-1">Description</h3>
          <p className="text-base-content">{game.description || 'No description provided'}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Players</h3>
            <p>{game.minPlayers}-{game.maxPlayers}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Duration</h3>
            <p>{game.duration ? `${game.duration} minutes` : 'N/A'}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Complexity</h3>
            <p className="capitalize">{game.complexity || 'N/A'}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Year Published</h3>
            <p>{game.yearPublished || 'N/A'}</p>
          </div>
        </div>

        {(game.designer || game.publisher) && (
          <div className="grid grid-cols-2 gap-4">
            {game.designer && (
              <div>
                <h3 className="font-semibold text-sm text-base-content/60 mb-1">Designer</h3>
                <p>{game.designer}</p>
              </div>
            )}
            {game.publisher && (
              <div>
                <h3 className="font-semibold text-sm text-base-content/60 mb-1">Publisher</h3>
                <p>{game.publisher}</p>
              </div>
            )}
          </div>
        )}

        {game.rules && (
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Rules</h3>
            <p className="text-sm">{game.rules}</p>
          </div>
        )}

        {game._count && (
          <div className="alert alert-info">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Used by {game._count.clubBoardGames || 0} clubs</span>
          </div>
        )}
      </div>

      <div className="divider"></div>

      <div className="flex gap-2">
        <button 
          className="btn btn-primary flex-1" 
          onClick={() => onEdit(game)}
          disabled={loading}
        >
          {loading ? <span className="loading loading-spinner"></span> : '✏️ Edit Game'}
        </button>
        <button 
          className="btn btn-error flex-1" 
          onClick={() => onDelete(game.id)}
          disabled={loading}
        >
          {loading ? <span className="loading loading-spinner"></span> : '🗑️ Delete Game'}
        </button>
      </div>
    </div>
  );
};

// Add/Edit Board Game Modal Component
const AddEditBoardGameModal = ({ isOpen, onClose, onSave, game, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rules: '',
    minPlayers: 2,
    maxPlayers: 4,
    duration: '',
    complexity: '',
    yearPublished: '',
    designer: '',
    publisher: '',
    coverImage: null
  });

  useEffect(() => {
    if (game) {
      setFormData({
        name: game.name || '',
        description: game.description || '',
        rules: game.rules || '',
        minPlayers: game.minPlayers || 2,
        maxPlayers: game.maxPlayers || 4,
        duration: game.duration || '',
        complexity: game.complexity || '',
        yearPublished: game.yearPublished || '',
        designer: game.designer || '',
        publisher: game.publisher || '',
        coverImage: null
      });
    } else {
      // Reset form for new game
      setFormData({
        name: '',
        description: '',
        rules: '',
        minPlayers: 2,
        maxPlayers: 4,
        duration: '',
        complexity: '',
        yearPublished: '',
        designer: '',
        publisher: '',
        coverImage: null
      });
    }
  }, [game, isOpen]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'coverImage') {
      setFormData(prev => ({ ...prev, coverImage: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl">
        <h3 className="font-bold text-lg mb-4">{game ? 'Edit Board Game' : 'Add New Board Game'}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Name *</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input input-bordered"
              required
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Description</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="textarea textarea-bordered h-24"
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Rules</span>
            </label>
            <textarea
              name="rules"
              value={formData.rules}
              onChange={handleChange}
              className="textarea textarea-bordered h-24"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Min Players</span>
              </label>
              <input
                type="number"
                name="minPlayers"
                value={formData.minPlayers}
                onChange={handleChange}
                className="input input-bordered"
                min="1"
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Max Players</span>
              </label>
              <input
                type="number"
                name="maxPlayers"
                value={formData.maxPlayers}
                onChange={handleChange}
                className="input input-bordered"
                min="1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Duration (minutes)</span>
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className="input input-bordered"
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Complexity</span>
              </label>
              <select
                name="complexity"
                value={formData.complexity}
                onChange={handleChange}
                className="select select-bordered"
              >
                <option value="">Select...</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Designer</span>
              </label>
              <input
                type="text"
                name="designer"
                value={formData.designer}
                onChange={handleChange}
                className="input input-bordered"
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Publisher</span>
              </label>
              <input
                type="text"
                name="publisher"
                value={formData.publisher}
                onChange={handleChange}
                className="input input-bordered"
              />
            </div>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Year Published</span>
            </label>
            <input
              type="number"
              name="yearPublished"
              value={formData.yearPublished}
              onChange={handleChange}
              className="input input-bordered"
              min="1900"
              max={new Date().getFullYear()}
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Cover Image</span>
            </label>
            <input
              type="file"
              name="coverImage"
              onChange={handleChange}
              className="file-input file-input-bordered"
              accept="image/*"
            />
            {game?.coverImage && (
              <label className="label">
                <span className="label-text-alt">Current image will be kept if no new image is uploaded</span>
              </label>
            )}
          </div>

          <div className="modal-action">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <span className="loading loading-spinner"></span> : (game ? 'Update Game' : 'Add Game')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Board Game Detail Component
const BoardGameDetail = ({ game, onApprove, onReject, loading }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{game.name}</h2>
        <div className="badge badge-warning">Pending Approval</div>
      </div>

      {game.coverImage && (
        <img src={game.coverImage} alt={game.name} className="w-full h-48 object-cover rounded-lg" />
      )}

      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-sm text-base-content/60 mb-1">Description</h3>
          <p className="text-base-content">{game.description || 'No description provided'}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Players</h3>
            <p>{game.minPlayers}-{game.maxPlayers}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Duration</h3>
            <p>{game.duration ? `${game.duration} minutes` : 'N/A'}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Complexity</h3>
            <p className="capitalize">{game.complexity || 'N/A'}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Year Published</h3>
            <p>{game.yearPublished || 'N/A'}</p>
          </div>
        </div>

        {(game.designer || game.publisher) && (
          <div className="grid grid-cols-2 gap-4">
            {game.designer && (
              <div>
                <h3 className="font-semibold text-sm text-base-content/60 mb-1">Designer</h3>
                <p>{game.designer}</p>
              </div>
            )}
            {game.publisher && (
              <div>
                <h3 className="font-semibold text-sm text-base-content/60 mb-1">Publisher</h3>
                <p>{game.publisher}</p>
              </div>
            )}
          </div>
        )}

        {game.rules && (
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Rules</h3>
            <p className="text-sm">{game.rules}</p>
          </div>
        )}

        {game.requestedBy && (
          <div className="alert alert-info">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Requested by {game.requestedBy.firstName} {game.requestedBy.lastName} ({game.requestedBy.email})</span>
          </div>
        )}
      </div>

      <div className="divider"></div>

      {!showRejectForm ? (
        <div className="flex gap-2">
          <button 
            className="btn btn-success flex-1" 
            onClick={() => onApprove(game.id)}
            disabled={loading}
          >
            {loading ? <span className="loading loading-spinner"></span> : '✓ Approve'}
          </button>
          <button 
            className="btn btn-error flex-1" 
            onClick={() => setShowRejectForm(true)}
            disabled={loading}
          >
            ✗ Reject
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Rejection Reason</span>
            </label>
            <textarea 
              className="textarea textarea-bordered h-24" 
              placeholder="Explain why this request is being rejected..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button 
              className="btn btn-error flex-1" 
              onClick={() => {
                onReject(game.id, rejectionReason);
                setShowRejectForm(false);
                setRejectionReason('');
              }}
              disabled={loading || !rejectionReason.trim()}
            >
              {loading ? <span className="loading loading-spinner"></span> : 'Confirm Rejection'}
            </button>
            <button 
              className="btn btn-ghost" 
              onClick={() => {
                setShowRejectForm(false);
                setRejectionReason('');
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Club Detail Component
const ClubDetail = ({ club, onApprove, onReject, loading }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{club.name}</h2>
        <div className="badge badge-warning">Pending Approval</div>
      </div>

      {club.image && (
        <img src={club.image} alt={club.name} className="w-full h-48 object-cover rounded-lg" />
      )}

      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-sm text-base-content/60 mb-1">Description</h3>
          <p className="text-base-content">{club.description || 'No description provided'}</p>
        </div>

        {club.location && (
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Location</h3>
            <p>📍 {club.location}</p>
          </div>
        )}

        {club.meetingDay && (
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Meeting Schedule</h3>
            <p>📅 {club.meetingDay}</p>
          </div>
        )}

        {club.games && (
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Games Offered</h3>
            <p>{club.games}</p>
          </div>
        )}
      </div>

      <div className="divider"></div>

      {!showRejectForm ? (
        <div className="flex gap-2">
          <button 
            className="btn btn-success flex-1" 
            onClick={() => onApprove(club.id)}
            disabled={loading}
          >
            {loading ? <span className="loading loading-spinner"></span> : '✓ Approve Club'}
          </button>
          <button 
            className="btn btn-error flex-1" 
            onClick={() => setShowRejectForm(true)}
            disabled={loading}
          >
            ✗ Reject
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Rejection Reason</span>
            </label>
            <textarea 
              className="textarea textarea-bordered h-24" 
              placeholder="Explain why this club is being rejected..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button 
              className="btn btn-error flex-1" 
              onClick={() => {
                onReject(club.id, rejectionReason);
                setShowRejectForm(false);
                setRejectionReason('');
              }}
              disabled={loading || !rejectionReason.trim()}
            >
              {loading ? <span className="loading loading-spinner"></span> : 'Confirm Rejection'}
            </button>
            <button 
              className="btn btn-ghost" 
              onClick={() => {
                setShowRejectForm(false);
                setRejectionReason('');
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Discussion Detail Component
const DiscussionDetail = ({ discussion, onTogglePin, onToggleLock, onDelete, loading }) => {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-2xl font-bold">{discussion.title}</h2>
          {discussion.isPinned && <span className="badge badge-info">📌 Pinned</span>}
          {discussion.isLocked && <span className="badge badge-error">🔒 Locked</span>}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-sm text-base-content/60 mb-1">Content</h3>
          <p className="text-base-content whitespace-pre-wrap">{discussion.content}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Upvotes</h3>
            <p>👍 {discussion.upvotes || 0}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Downvotes</h3>
            <p>👎 {discussion.downvotes || 0}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Replies</h3>
            <p>💬 {discussion.replyCount || 0}</p>
          </div>
          <div>
            <h3 className="font-semibold text-sm text-base-content/60 mb-1">Views</h3>
            <p>👁️ {discussion.views || 0}</p>
          </div>
        </div>

        {discussion.author && (
          <div className="alert alert-info">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Posted by {discussion.author.firstName} {discussion.author.lastName}</span>
          </div>
        )}
      </div>

      <div className="divider"></div>

      <div className="space-y-2">
        <button 
          className={`btn btn-outline w-full ${discussion.isPinned ? 'btn-warning' : 'btn-info'}`}
          onClick={() => onTogglePin(discussion.id)}
          disabled={loading}
        >
          {loading ? <span className="loading loading-spinner"></span> : discussion.isPinned ? '📌 Unpin Discussion' : '📌 Pin Discussion'}
        </button>
        
        <button 
          className={`btn btn-outline w-full ${discussion.isLocked ? 'btn-success' : 'btn-warning'}`}
          onClick={() => onToggleLock(discussion.id)}
          disabled={loading}
        >
          {loading ? <span className="loading loading-spinner"></span> : discussion.isLocked ? '🔓 Unlock Discussion' : '🔒 Lock Discussion'}
        </button>
        
        <button 
          className="btn btn-error w-full"
          onClick={() => onDelete(discussion.id)}
          disabled={loading}
        >
          {loading ? <span className="loading loading-spinner"></span> : '🗑️ Delete Discussion'}
        </button>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
